import { Button, Card, DatePicker, Divider, Flex, Space, Spin } from "antd";
import { getJobSummary } from "api/services/JobSummaryService";
import {
  Addendum,
  Approved,
  TJobSummary,
  VoiceFileStatus,
  VoiceFileType,
  VoiceStatFlag
} from "api/services/models/types";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { useLayoutContext } from "components/MainContainer";
import SvgOverlay from "components/SvgOverlay";
import UploadModal from "components/UploadJob";
import dayjs, { Dayjs } from "dayjs";
import { ReactComponent as AddSquareIcon } from "Icons/vuesax/add-square.svg";
import { useCallback, useEffect, useState } from "react";
import { ReactComponent as ArrowLeftIcon } from "../../Icons/vuesax/arrow-left.svg";
import { ReactComponent as ArrowRigthIcon } from "../../Icons/vuesax/arrow-right.svg";
import { ReactComponent as BookIcon } from "../../Icons/vuesax/book-1.svg";
import { ReactComponent as CalendarIcon } from "../../Icons/vuesax/calendar.svg";
import { ReactComponent as ChartIcon } from "../../Icons/vuesax/chart-21.svg";
import { ReactComponent as InfoCircleIcon } from "../../Icons/vuesax/info-circle.svg";
import { ReactComponent as HamburgerIcon } from "../../Icons/vuesax/menu.svg";
import { ReactComponent as MessageTextIcon } from "../../Icons/vuesax/message-text.svg";
import { ReactComponent as MessagesIcon } from "../../Icons/vuesax/messages.svg";
import { ReactComponent as NotificationCircleIcon } from "../../Icons/vuesax/notification-circle.svg";
import { ReactComponent as TickCircleIcon } from "../../Icons/vuesax/tick-circle.svg";
import JobSummaryGrid from "./JobSummaryGrid";

const JobSummary = () => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [summaryDate, setSummaryDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [summaryData, setSummaryData] = useState<TJobSummary[]>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const [totals, setTotals] = useState<Record<string, number>>({
    totalDictations: 0,
    totalDictationsAddendums: 0,
    completed: 0,
    completedAddendums: 0,
    pendingPr: 0,
    pendingPrAddendums: 0,
    pendingTr: 0,
    pendingTrAddendums: 0,
    blankOrInvalid: 0,
    pendingApproval: 0,
    pendingApprovalAddendums: 0,
    stat: 0,
    statAddendums: 0
  });

  const minDate = dayjs().startOf("day").subtract(90, "day");
  const maxDate = dayjs().startOf("day");
  const { webDUser } = useTDAuthentication();

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("blur");
    } else {
      document.body.classList.remove("blur");
    }

    return () => {
      document.body.classList.remove("blur");
    };
  }, [isModalOpen]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const reload = () => {
    if (accountId) {
      setLoading(true);
      setSummaryData([]);
      getJobSummary(accountId, summaryDate)
        .then(data => {
          setSummaryData(data);
          let calculatedTotals = data.reduce((op, summary) => {
            const isAddendum = summary.type === VoiceFileType.Addendum;
            op.totalDictations = (op.totalDictations ?? 0) + 1;
            op.totalDictationsAddendums =
              (op.totalDictationsAddendums ?? 0) + (isAddendum ? 1 : 0);
            op.completed = (op.completed ?? 0) + isCompleted(summary);
            op.completedAddendums =
              (op.completedAddendums ?? 0) + (isCompleted(summary) && isAddendum ? 1 : 0);
            op.pendingPr = (op.pendingPr ?? 0) + isPendingPr(summary);
            op.pendingPrAddendums =
              (op.pendingPrAddendums ?? 0) + (isPendingPr(summary) && isAddendum ? 1 : 0);
            op.pendingTr = (op.pendingTr ?? 0) + isPendingTr(summary);
            op.pendingTrAddendums =
              (op.pendingTrAddendums ?? 0) + (isPendingTr(summary) && isAddendum ? 1 : 0);
            op.blankOrInvalid = (op.blankOrInvalid ?? 0) + blankOrInvalid(summary);
            op.pendingApproval = (op.pendingApproval ?? 0) + pendingApproval(summary);
            op.pendingApprovalAddendums =
              (op.pendingApprovalAddendums ?? 0) +
              (pendingApproval(summary) && isAddendum ? 1 : 0);
            op.stat = (op.stat ?? 0) + isSTAT(summary);
            op.statAddendums =
              (op.statAddendums ?? 0) + (isSTAT(summary) && isAddendum ? 1 : 0);
            return op;
          }, {} as Record<string, number>);
          if (data.length === 0) {
            calculatedTotals = {
              totalDictations: 0,
              totalDictationsAddendums: 0,
              completed: 0,
              completedAddendums: 0,
              pendingPr: 0,
              pendingPrAddendums: 0,
              pendingTr: 0,
              pendingTrAddendums: 0,
              blankOrInvalid: 0,
              pendingApproval: 0,
              pendingApprovalAddendums: 0,
              stat: 0,
              statAddendums: 0
            };
          }

          setTotals(calculatedTotals);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    reload();
  }, [summaryDate, accountId]);

  const onChange = useCallback((newSummaryDate: Dayjs) => setSummaryDate(newSummaryDate), []);

  const onUploadClose = () => {
    setIsModalOpen(false);
    reload();
  };

  const hasTranscripts = (summary: TJobSummary) => (summary.transcripts?.length ?? 0) > 0;
  const isComplete = (summary: TJobSummary) =>
    summary.voiceFileStatus === VoiceFileStatus.Completed;
  const isValid = (summary: TJobSummary) => summary.type === VoiceFileType.Valid;
  const isAddendum = (summary: TJobSummary) => summary.type === VoiceFileType.Addendum;

  const isCompleted = (summary: TJobSummary) =>
    isComplete(summary) && hasTranscripts(summary) ? 1 : 0;

  const isPendingPr = (summary: TJobSummary) =>
    isComplete(summary) && isValid(summary) && !hasTranscripts(summary) ? 1 : 0;

  const isPendingTr = (summary: TJobSummary) =>
    !isComplete(summary) && isValid(summary) ? 1 : 0;

  const blankOrInvalid = (summary: TJobSummary) =>
    !isComplete(summary) && !isValid(summary) && !isAddendum(summary) ? 1 : 0;

  const pendingApproval = (summary: TJobSummary) =>
    isComplete(summary) &&
    hasTranscripts(summary) &&
    summary.transcripts?.some(t => t.approved === Approved.No)
      ? 1
      : 0;
  const isSTAT = (summary: TJobSummary) => (summary.statFlag === VoiceStatFlag.Stat ? 1 : 0);

  const getFormattedDate = (date: Dayjs | null) => {
    if (date) {
      return date.format("dddd, D MMMM");
    }
    return "Select a date";
  };

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  return (
    <>
      {!isSmallScreen ? (
        <>
          {isModalOpen && <div className="blur" />}
          <div className="container">
            <div className="header">
              <div className="header-title">
                {isSmallScreen && (
                  <Button
                    type="text"
                    icon={<HamburgerIcon />}
                    onClick={toggleDrawer}
                    className="hamburger-menu-button"
                    style={{ marginRight: ".25rem" }}
                  />
                )}
                <span>Job Summary</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                {webDUser?.hasPrivilege(WebDPrivilege.UploadVoiceFiles) && (
                  <Button
                    style={{ marginRight: "1.5rem" }}
                    onClick={() => setIsModalOpen(true)}
                    type="primary">
                    <AddSquareIcon width="16px" height="16px" />
                    Add New Job
                  </Button>
                )}

                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      marginRight: "0.5rem"
                    }}>
                    Date:
                  </span>
                  <DatePicker
                    value={summaryDate}
                    className="custom-datepicker"
                    maxDate={maxDate}
                    minDate={minDate}
                    allowClear={false}
                    onChange={onChange}
                    suffixIcon={<CalendarIcon className="calendar-icon" />}
                    disabled={loading}
                  />
                </div>
                {<UploadModal onClose={onUploadClose} open={isModalOpen} />}
              </div>
            </div>
            <div className="card-container">
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#FFB876"
                    overlaySvg={<BookIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Dictations / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.totalDictations - totals.totalDictationsAddendums} /{" "}
                      {totals.totalDictationsAddendums}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#9AFF76"
                    overlaySvg={<TickCircleIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Completed Jobs / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.completed - totals.completedAddendums} /{" "}
                      {totals.completedAddendums}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#FFE176"
                    overlaySvg={<MessageTextIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Pending Proofreading / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.pendingPr - totals.pendingPrAddendums} /{" "}
                      {totals.pendingPrAddendums}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#76D6FF"
                    overlaySvg={<MessagesIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Pending Transcription / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.pendingTr - totals.pendingTrAddendums} /{" "}
                      {totals.pendingTrAddendums}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#FF9797"
                    overlaySvg={<InfoCircleIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Invalid
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.blankOrInvalid}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#FF9FF5"
                    overlaySvg={<NotificationCircleIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Pending Approval / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.pendingApproval - totals.pendingApprovalAddendums} /{" "}
                      {totals.pendingApprovalAddendums}
                    </span>
                  </div>
                </div>
              </Card>
              <Card className="card" styles={{ body: { padding: "1rem" } }}>
                <div className="card-content">
                  <SvgOverlay
                    color="#CAA9FF"
                    overlaySvg={<ChartIcon width="1.25rem" height="1.25rem" />}
                  />
                  <div className="card-text">
                    <span className="font-777" style={{ marginBottom: ".25rem" }}>
                      Stat / Addendums
                    </span>
                    <span className="font-1212" style={{ marginBottom: "1rem" }}>
                      {totals.stat - totals.statAddendums} / {totals.statAddendums}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
            <Card className="card-style" styles={{ body: { padding: "0px" } }}>
              {loading && (
                <Flex
                  justify="space-around"
                  style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
                  <Spin tip="Loading, please wait..." />
                </Flex>
              )}
              {error && <ErrorViewer error={error} />}
              {!loading && summaryData && <JobSummaryGrid data={summaryData} />}
            </Card>
          </div>
        </>
      ) : (
        <div className="container">
          <div className="header">
            <div className="header-title">
              {isSmallScreen && (
                <Button
                  type="text"
                  icon={<HamburgerIcon />}
                  onClick={toggleDrawer}
                  className="hamburger-menu-button"
                  style={{ marginRight: ".25rem" }}
                />
              )}
              <span style={{ textWrap: "nowrap" }}>Job Summary</span>
              {webDUser?.hasPrivilege(WebDPrivilege.UploadVoiceFiles) && (
                <Button
                  style={{  margin: "0 1rem" }}
                  onClick={() => setIsModalOpen(true)}
                  type="primary">
                  <AddSquareIcon width="1rem" height="1rem" />
                  <span style={{ fontSize: ".875rem", fontWeight: "400" }}>Add New Job</span>
                </Button>
              )}
            </div>
          </div>
          {isModalOpen && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "#FFF",
                zIndex: 9999,
                overflow: "auto"
              }}>
              <UploadModal onClose={onUploadClose} open={isModalOpen} />
            </div>
          )}
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Button
              type="text"
              icon={
                <ArrowLeftIcon
                  width={"1.5rem"}
                  height={"1.5rem"}
                  style={{ color: "#1868db" }}
                />
              }
              style={{ marginRight: "10px", padding: 0 }}
              onClick={() => {
                if (summaryDate) {
                  setSummaryDate(summaryDate.subtract(1, "day"));
                }
              }}
            />
            <Space
              direction="horizontal"
              size={12}
              onClick={() => setDatePickerOpen(true)}
              style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1rem", fontWeight: "500" }} className="font-1212">
                <div
                  style={{
                    display: "Flex",
                    justifyContent: "flex-start",
                    alignItems: "center"
                  }}>
                  <CalendarIcon
                    className="calendar-icon"
                    style={{ color: "#1868db", marginRight: ".37rem" }}
                  />{" "}
                  {getFormattedDate(summaryDate)}
                </div>
                <DatePicker
                  placement="bottomLeft"
                  value={summaryDate}
                  maxDate={maxDate}
                  minDate={minDate}
                  allowClear={false}
                  onChange={onChange}
                  disabled={loading}
                  format="dddd, D MMMM"
                  style={{ visibility: "hidden", position: "absolute" }}
                  popupStyle={{ zIndex: 9999 }}
                  open={datePickerOpen}
                  onOpenChange={setDatePickerOpen}
                />
              </div>
            </Space>

            <Button
              type="text"
              icon={
                <ArrowRigthIcon
                  width={"1.5rem"}
                  height={"1.5rem"}
                  style={{ color: "#1868db" }}
                />
              }
              style={{ marginLeft: "10px", padding: 0 }}
              onClick={() => {
                if (summaryDate) {
                  setSummaryDate(summaryDate.add(1, "day"));
                }
              }}
            />
          </div>
          <Divider />
          <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <div
              style={{
                flexGrow: 1,
                overflowY: "auto",
                backgroundColor: "#FAFAFA",
                marginBottom: "3.2rem"
              }}>
              <div className="card-container-mobile">
                <Card className="card-mobile" styles={{ body: { padding: ".75rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FFB876"
                      overlaySvg={<BookIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Dictations / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.totalDictations - totals.totalDictationsAddendums} /{" "}
                        {totals.totalDictationsAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card className="card-mobile" styles={{ body: { padding: ".75rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#9AFF76"
                      overlaySvg={<TickCircleIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Completed Jobs / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.completed - totals.completedAddendums} /{" "}
                        {totals.completedAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card
                  className="card-mobile"
                  styles={{ body: { padding: ".75rem" } }}
                  style={{ gridRowStart: 2 }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FFE176"
                      overlaySvg={
                        <MessageTextIcon
                          width="1.25rem"
                          height="1.25rem"
                          style={{ marginRight: "-0.5rem" }}
                        />
                      }
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Pending Proofreading / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.pendingPr - totals.pendingPrAddendums} /{" "}
                        {totals.pendingPrAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card
                  className="card-mobile"
                  styles={{ body: { padding: ".75rem" } }}
                  style={{ gridRowStart: 2 }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#76D6FF"
                      overlaySvg={
                        <MessagesIcon
                          width="1.25rem"
                          height="1.25rem"
                          style={{ marginRight: "-0.5rem" }}
                        />
                      }
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Pending Transcription / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.pendingTr - totals.pendingTrAddendums} /{" "}
                        {totals.pendingTrAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card
                  className="card-mobile"
                  styles={{ body: { padding: ".75rem" } }}
                  style={{ gridRowStart: 3 }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FF9797"
                      overlaySvg={<InfoCircleIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Invalid
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.blankOrInvalid ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card
                  className="card-mobile"
                  styles={{ body: { padding: ".75rem" } }}
                  style={{ gridRowStart: 3 }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FF9FF5"
                      overlaySvg={
                        <NotificationCircleIcon
                          width="1.25rem"
                          height="1.25rem"
                          style={{ marginRight: "-0.5rem" }}
                        />
                      }
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Pending Approval / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.pendingApproval - totals.pendingApprovalAddendums} /{" "}
                        {totals.pendingApprovalAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card
                  className="card-mobile"
                  styles={{ body: { padding: ".75rem" } }}
                  style={{ gridRowStart: 4 }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#CAA9FF"
                      overlaySvg={<ChartIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Stat / Addendums
                      </span>
                      <span className="font-1212" style={{ marginBottom: ".75rem" }}>
                        {totals.stat - totals.statAddendums} / {totals.statAddendums}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
              <Divider />
              {loading && (
                <Flex
                  justify="space-around"
                  style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
                  <Spin tip="Loading, please wait..." />
                </Flex>
              )}
              {error && <ErrorViewer error={error} />}
              {!loading && summaryData && <JobSummaryGrid data={summaryData} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobSummary;
